import { assessmentData } from '../data/assessmentData';
import * as XLSX from 'xlsx';
import { useEffect } from 'react';

function Results({ answers, companyInfo }) {
  // Add useEffect to scroll to top when Results component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { companyName, assessmentDate, displayDate } = companyInfo;
  
  // Check if the app is running in an iframe
  const isEmbedded = window !== window.parent;

  const getRatingLabel = (value) => {
    const ratings = {
      1: 'Strongly Disagree',
      2: 'Disagree',
      3: 'Neutral',
      4: 'Agree',
      5: 'Strongly Agree'
    };
    return ratings[value];
  };

  const generateExportData = () => {
    const data = [];
    
    // Add company info and email
    data.push(['Company Name', companyName]);
    data.push(['Email', companyInfo.email]);
    data.push(['Assessment Date', assessmentDate]);
    data.push([]);
    
    // Add header row
    data.push(['Section', 'Question', 'Response']);
    
    // Add data rows
    assessmentData.sections.forEach(section => {
      section.questions.forEach((question, index) => {
        const answerValue = answers[`${section.id}-${index}`];
        data.push([
          `Part ${section.id}: ${section.title}`,
          question,
          getRatingLabel(answerValue)
        ]);
      });
    });

    // Add empty row for spacing
    data.push([]);
    data.push([]);

    // Calculate total score
    const allScores = Object.values(answers).filter(score => score > 0);
    const totalScore = Math.round((allScores.reduce((a, b) => a + b, 0) / (allScores.length * 5)) * 100);

    // Get score interpretation
    const interpretation = assessmentData.interpretations.find(
      interp => totalScore >= interp.range[0] && totalScore <= interp.range[1]
    );

    // Add total score and interpretation
    data.push(['Overall Score', `${totalScore}%`]);
    data.push(['Score Interpretation', interpretation?.label || 'A Starting Point']);
    data.push(['Interpretation Details', interpretation?.description || '']);
    
    return data;
  };

  const downloadAsXLSX = () => {
    const data = generateExportData();
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assessment Results');
    
    // Auto-size columns
    const colWidths = data.reduce((acc, row) => {
      row.forEach((cell, i) => {
        const cellLength = cell?.toString().length || 0;
        acc[i] = Math.max(acc[i] || 0, cellLength);
      });
      return acc;
    }, {});
    
    ws['!cols'] = Object.values(colWidths).map(width => ({ width }));
    
    XLSX.writeFile(wb, 'predictable-performance-assessment.xlsx');
  };

  const switchToHubSpot = () => {
    const cleanCompanyName = companyInfo.companyName?.trim() || '';
    const cleanEmail = companyInfo.email?.trim() || '';

    // Get URL parameters from the initial link
    const urlParams = new URLSearchParams(window.location.search);
    const initialParams = {};
    
    // Check for specific parameters to preserve
    ['firstname', 'lastname', 'email', 'company'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        initialParams[param] = value;
      }
    });

    // Helper function to get first three words
    const getFirstThreeWords = (text) => {
      const words = text.split(' ').slice(0, 3).join(' ');
      return words + (text.split(' ').length > 3 ? '...' : '');
    };

    // Calculate total and section scores
    const sectionScores = assessmentData.sections.reduce((acc, section) => {
      const sectionTotal = section.questions.reduce((total, _, index) => {
        const score = answers[`${section.id}-${index}`] || 0;
        return total + score;
      }, 0);
      acc[section.id] = sectionTotal;
      return acc;
    }, {});

    const allScores = Object.values(answers).filter(score => score > 0);
    const totalScore = Math.round((allScores.reduce((a, b) => a + b, 0) / (allScores.length * 5)) * 100);

    // Create a text summary of the assessment with scores
    let questionCounter = 1;
    const textSummary = `TOTAL: ${totalScore}\n\n` + assessmentData.sections.map(section => {
      const sectionTotal = sectionScores[section.id];
      const sectionAnswers = section.questions.map((question, index) => {
        const score = answers[`${section.id}-${index}`];
        const shortQuestion = getFirstThreeWords(question);
        const result = `Q${questionCounter} ${shortQuestion}: ${score || 0}`;
        questionCounter++;
        return result;
      }).join('\n');
      
      return `${section.title.toUpperCase()}: ${sectionTotal}\n\n${sectionAnswers}`;
    }).join('\n\n');

    // Construct the HubSpot URL with parameters
    let baseUrl = `https://meetings.hubspot.com/databox-meeting/predictability-index?${isEmbedded ? 'embed=true&' : ''}`;
    
    // Use email and company from URL parameters if available, otherwise use form values
    const finalCompanyName = initialParams.company || cleanCompanyName;
    const finalEmail = initialParams.email || cleanEmail;
    
    // Add company and email parameters
    baseUrl += `company=${encodeURIComponent(finalCompanyName)}&email=${encodeURIComponent(finalEmail)}`;
    
    // Add firstname and lastname if they exist in the initial URL
    if (initialParams.firstname) {
      baseUrl += `&firstname=${encodeURIComponent(initialParams.firstname)}`;
    }
    
    if (initialParams.lastname) {
      baseUrl += `&lastname=${encodeURIComponent(initialParams.lastname)}`;
    }

    const encodedSummary = encodeURIComponent(textSummary);
    const finalUrl = `${baseUrl}&predictability_index=${encodedSummary}`;

    if (!isEmbedded) {
      window.open(finalUrl, '_blank');
      return;
    }

    window.parent.postMessage({ type: "replaceContent", html: getHubSpotFormHTML(finalUrl) }, "*");
  };

  // Helper function to generate HubSpot form HTML
  const getHubSpotFormHTML = (url) => `
    <div class="form">
      <div class="meeting">
        <div class="meetings-iframe-container" data-src="${url}"></div>
      </div>
      <div class="legal dbx-container dbx-title-text__text">
        By submitting the completed form, you agree to our 
        <a href="/terms-of-service" target="_blank">Terms of Service</a> and 
        <a href="/privacy-policy" target="_blank">Privacy Policy</a>.
      </div>
    </div>
    <script>
      setTimeout(() => {
        const script = document.createElement('script');
        script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
        script.async = true;
        document.body.appendChild(script);
      }, 100);
    </script>
  `;

  const getScoreInterpretation = (score) => {
    const interpretation = assessmentData.interpretations.find(
      interp => score >= interp.range[0] && score <= interp.range[1]
    );
    return interpretation?.label || "A Starting Point";
  };

  const getScoreMessage = (score) => {
    const interpretation = assessmentData.interpretations.find(
      interp => score >= interp.range[0] && score <= interp.range[1]
    );
    return interpretation?.description || assessmentData.interpretations[2].description;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
            Assessment Results
          </h2>

          <div className="text-center mb-12">
            <div className="mb-8">
              <div className="text-[80px] font-bold text-primary leading-none">
                {Math.round((Object.values(answers).filter(score => score > 0).reduce((a, b) => a + b, 0) / (Object.values(answers).filter(score => score > 0).length * 5)) * 100)}%
              </div>
              <h3 className="text-2xl font-semibold text-gray-700 mt-2">
                {getScoreInterpretation(Math.round((Object.values(answers).filter(score => score > 0).reduce((a, b) => a + b, 0) / (Object.values(answers).filter(score => score > 0).length * 5)) * 100))}
              </h3>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                {getScoreMessage(Math.round((Object.values(answers).filter(score => score > 0).reduce((a, b) => a + b, 0) / (Object.values(answers).filter(score => score > 0).length * 5)) * 100))}
              </p>
            </div>

            {isEmbedded ? (
              <p className="text-gray-600">
                <button
                  onClick={switchToHubSpot}
                  className="text-primary hover:text-primary/90 font-medium"
                >
                  Book a call
                </button>
                {" to learn how Databox can help you drive more predictable performance,"}
                <br />
                {"or "}
                <a
                  href="https://academy.databox.com/predictable-performance-methodology"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/90 font-medium"
                >
                  take our free Predictable Performance Methodology course.
                </a>
              </p>
            ) : (
              <p className="text-gray-600">
                <button
                  onClick={downloadAsXLSX}
                  className="text-primary hover:text-primary/90 font-medium"
                >
                  Download this report
                </button>
                {" and share it with your rep or "}
                <button
                  onClick={switchToHubSpot}
                  className="text-primary hover:text-primary/90 font-medium"
                >
                  Book a consultation
                </button>
              </p>
            )}
          </div>

          <div className="border-t border-gray-200 mb-8"></div>

          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="space-y-1">
                <h3 className="text-xl font-semibold">Your Responses</h3>
                <p className="text-sm text-gray-500">
                  {companyName && `${companyName}`}
                  {companyName && displayDate && ' - '}
                  {displayDate}
                </p>
              </div>
              
              {/* Download button next to "Your Responses" for embedded version */}
              {isEmbedded && (
                <button
                  onClick={downloadAsXLSX}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Report
                </button>
              )}
            </div>

            {assessmentData.sections.map((section) => (
              <div key={section.id} className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3 flex justify-between items-center">
                  <span>Part {section.id}: {section.title}</span>
                  <span className="text-base text-gray-600">
                    {section.questions.reduce((total, _, index) => {
                      const score = answers[`${section.id}-${index}`] || 0;
                      return total + score;
                    }, 0)} points
                  </span>
                </h4>
                <div className="space-y-4">
                  {section.questions.map((question, index) => {
                    const answerValue = answers[`${section.id}-${index}`];
                    return (
                      <div 
                        key={index} 
                        className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center gap-4"
                      >
                        <div className="flex-grow">
                          <p className="text-gray-700">{question}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`
                            w-32 text-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap
                            ${answerValue >= 4 ? 'bg-green-100 text-green-800' : 
                              answerValue === 3 ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-red-100 text-red-800'}
                          `}>
                            {getRatingLabel(answerValue)}
                          </div>
                          <div className="text-lg font-semibold text-gray-700 w-8 text-center">
                            {answerValue}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center w-full mt-8">
            {/* Left: Back to Start as a link */}
            <div>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back to Start
              </button>
            </div>
            
            {/* Middle: Book a Call/Consultation as a button */}
            <div className="flex justify-center">
              {isEmbedded ? (
                <button
                  onClick={switchToHubSpot}
                  className="flex items-center gap-2 px-5 py-2 text-sm text-white bg-primary border border-primary rounded-md hover:bg-primary/90 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  Book a Call
                </button>
              ) : (
                <button
                  onClick={switchToHubSpot}
                  className="flex items-center gap-2 px-5 py-2 text-sm text-white bg-primary border border-primary rounded-md hover:bg-primary/90 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  Book a Consultation
                </button>
              )}
            </div>
            
            {/* Right: Download Report or Take Our Free Course as a link */}
            <div>
              {!isEmbedded ? (
                <button
                  onClick={downloadAsXLSX}
                  className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Report
                </button>
              ) : (
                <a
                  href="https://academy.databox.com/predictable-performance-methodology"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                  Take Our Free Course
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Results; 